/**
 * Project Pricing Configuration
 * Based on YouTube video pricing model: Build fee + 20% monthly retainer
 *
 * Build Fee: $500 - $5,000 (one-time, based on complexity)
 * Monthly Retainer: 20% of Build Fee (recurring)
 */

export const PROJECT_PRICING = {
  // Fixed retainer percentage (20% of build fee)
  RETAINER_PERCENTAGE: 0.20,

  // Pricing tiers by location
  TIERS: {
    PH: {
      id: 'PH',
      name: 'Philippines',
      currency: 'PHP',
      buildMin: 25000,     // ₱25,000
      buildMax: 250000,    // ₱250,000
      complexityLevels: {
        low: {
          buildFee: 25000,
          description: 'Simple app with basic features',
        },
        medium: {
          buildFee: 100000,
          description: 'Standard app with moderate complexity',
        },
        high: {
          buildFee: 250000,
          description: 'Complex enterprise-level application',
        },
      },
    },
    GLOBAL: {
      id: 'GLOBAL',
      name: 'Global',
      currency: 'USD',
      buildMin: 500,       // $500
      buildMax: 5000,      // $5,000
      complexityLevels: {
        low: {
          buildFee: 500,
          description: 'Simple app with basic features',
        },
        medium: {
          buildFee: 2000,
          description: 'Standard app with moderate complexity',
        },
        high: {
          buildFee: 5000,
          description: 'Complex enterprise-level application',
        },
      },
    },
  },

  // Country to tier mapping
  COUNTRY_TIER_MAP: {
    PH: 'PH',
    // Add more countries as needed
    // Default is GLOBAL
  },
};

/**
 * Calculate monthly retainer from build fee
 * @param {number} buildFee - One-time build fee
 * @returns {number} Monthly retainer amount
 */
export const calculateRetainer = (buildFee) => {
  return buildFee * PROJECT_PRICING.RETAINER_PERCENTAGE;
};

/**
 * Get pricing tier for a country
 * @param {string} countryCode - ISO 2-letter country code
 * @returns {object} Pricing tier configuration
 */
export const getTierForCountry = (countryCode) => {
  const tierId = PROJECT_PRICING.COUNTRY_TIER_MAP[countryCode] || 'GLOBAL';
  return PROJECT_PRICING.TIERS[tierId];
};

/**
 * Get suggested pricing for a project
 * @param {string} countryCode - ISO 2-letter country code
 * @param {string} complexity - 'low' | 'medium' | 'high'
 * @returns {object} Suggested pricing with buildFee and monthlyRetainer
 */
export const getSuggestedPricing = (countryCode, complexity = 'medium') => {
  const tier = getTierForCountry(countryCode);
  const level = tier.complexityLevels[complexity] || tier.complexityLevels.medium;

  return {
    tier: tier.id,
    tierName: tier.name,
    currency: tier.currency,
    complexity,
    buildFee: level.buildFee,
    monthlyRetainer: calculateRetainer(level.buildFee),
    description: level.description,
  };
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (PHP, USD, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  const locale = currency === 'PHP' ? 'en-PH' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Get all available complexity levels for display
 * @param {string} tierId - Tier ID ('PH' or 'GLOBAL')
 * @returns {array} Array of complexity options with pricing
 */
export const getComplexityOptions = (tierId = 'GLOBAL') => {
  const tier = PROJECT_PRICING.TIERS[tierId];
  if (!tier) return [];

  const options = Object.entries(tier.complexityLevels).map(([key, level]) => ({
    id: key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
    buildFee: level.buildFee,
    monthlyRetainer: calculateRetainer(level.buildFee),
    description: level.description,
    formattedBuildFee: formatCurrency(level.buildFee, tier.currency),
    formattedRetainer: formatCurrency(calculateRetainer(level.buildFee), tier.currency),
  }));

  // Add custom option for manual entry beyond presets
  options.push({
    id: 'custom',
    label: 'Custom',
    buildFee: null,
    monthlyRetainer: null,
    description: 'Enter a custom amount',
    formattedBuildFee: 'Manual',
    formattedRetainer: 'Manual',
  });

  return options;
};

export default PROJECT_PRICING;
